import React, { useCallback, useEffect, useMemo, useState } from "react";
import Sidebar from "../../layout/Sidebar/SideBar";
import Navbar from "../../layout/Navbar/Navbar";
import blogFetch from "../../axios/config";
import { FormatDate } from "../../utils/DataUtils";
import { useDebouncedValue } from "../fotolito/DataTable";

interface AcoPreMontagem {
    ordem_Serial: string;
    set_Aual: string | null;
    ordem_Aco: string;
    data_Saida: string | null;
    saida_User: string | null;
    hora_UltimoApont: string | null;
}


function porDataSaidaDesc(a: AcoPreMontagem, b: AcoPreMontagem): number {
    const dataA = a.data_Saida ? new Date(a.data_Saida).getTime() : 0;
    const dataB = b.data_Saida ? new Date(b.data_Saida).getTime() : 0;
    return dataB - dataA;
}

export function AcosPreMontagem(): JSX.Element {
    const [acos, setAcos] = useState<AcoPreMontagem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchInput, setSearchInput] = useState("");
    const pesquisa = useDebouncedValue(searchInput);

    const fetchAcos = useCallback(async () => {
        setIsLoading(true);
        try {
            const { data } = await blogFetch.get<AcoPreMontagem[]>("ISAGA_ApontamentoPreMontagem/acos");
            setAcos(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Erro ao buscar aços na pré-montagem:", error);
            setAcos([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAcos();
    }, [fetchAcos]);


    const linhas = useMemo(
        () => [...acos].sort(porDataSaidaDesc).map((aco, index) => ({ ...aco, id: index })),
        [acos]
    );

    const linhasFiltradas = useMemo(() => {
        const termo = pesquisa.trim().toLowerCase();
        if (!termo) return linhas;
        return linhas.filter(linha =>
            String(linha.ordem_Serial ?? "").toLowerCase().includes(termo) ||
            String(linha.ordem_Aco ?? "").toLowerCase().includes(termo)
        );
    }, [linhas, pesquisa]);

    return (
        <div className="d-flex app_">
            <Sidebar />
            <Navbar />
            <div className="flex-grow-1">
                <div className="container-fluid mt-3 app_body">
                    <h4 className="fw-bold text-primary mb-3">Aços na Pré-Montagem sem Apontamento</h4>

                    <div className="card shadow-sm mb-4">
                        <div className="card-body">
                            <div className="d-flex flex-wrap gap-3 align-items-end">
                                <div>
                                    <label className="form-label small fw-semibold text-muted mb-1">
                                        Pesquisar
                                    </label>
                                    <div className="input-group input-group-sm shadow-sm" style={{ minWidth: 260 }}>
                                        <span className="input-group-text"><i className="bi bi-search" /></span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Ordem da peça ou ordem do aço..."
                                            value={searchInput}
                                            onChange={(e) => setSearchInput(e.target.value)}
                                        />
                                        {searchInput && (
                                            <button
                                                className="btn btn-outline-secondary"
                                                onClick={() => setSearchInput("")}
                                                title="Limpar pesquisa"
                                            >
                                                <i className="bi bi-x-lg" />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <button
                                    className="btn btn-sm btn-outline-primary align-self-end"
                                    onClick={fetchAcos}
                                    disabled={isLoading}
                                    title="Atualizar lista"
                                >
                                    {isLoading ? (
                                        <span className="spinner-border spinner-border-sm me-1" />
                                    ) : (
                                        <i className="bi bi-arrow-clockwise me-1" />
                                    )}
                                    Atualizar
                                </button>

                                <div className="ms-auto align-self-end text-muted small">
                                    {linhasFiltradas.length} aço(s)
                                    {linhasFiltradas.length !== linhas.length && ` de ${linhas.length}`}
                                </div>
                            </div>
                        </div>
                    </div>

                    {isLoading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status" />
                            <p className="text-muted mt-2">Carregando aços...</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-striped table-hover align-middle fw-bold" style={{ fontSize: "1.1rem" }}>
                                <thead className="table-primary">
                                    <tr>
                                        <th>Ordem</th>
                                        <th>Ordem Aço</th>
                                        <th>Setor</th>
                                        <th>Data Saída</th>
                                        <th>Saída por</th>
                                        <th>Últ. Apontamento</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {linhasFiltradas.length === 0 ? (
                                        <tr>
                                            <td colSpan={6} className="text-center text-muted">
                                                Nenhum aço encontrado
                                            </td>
                                        </tr>
                                    ) : (
                                        linhasFiltradas.map((linha) => (
                                            <tr key={linha.id}>
                                                <td>{linha.ordem_Serial}</td>
                                                <td>{linha.ordem_Aco}</td>
                                                <td>{linha.set_Aual || "-"}</td>
                                                <td>{linha.data_Saida ? FormatDate(linha.data_Saida) : "-"}</td>
                                                <td>{linha.saida_User || "-"}</td>
                                                <td>{linha.hora_UltimoApont || "-"}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default AcosPreMontagem;
